// The marina's own calendar (/events): plain data rules, no Firebase, so they can be unit-tested
// (tests/unit/events.test.mjs). Dates are marina wall-clock days ('YYYY-MM-DD') and times
// ('HH:MM'), never instants, so an event never moves a day for someone in another time zone.
//
// /events/{id}: { title, date, endDate?, allDay, start?, end?, repeat, repeatUntil?, skip?: { day: true },
//                 category, location?, notes?, createdBy, createdAt, updatedBy, updatedAt }

export const EVENT_CATEGORIES = [
  { id: 'marina', label: 'Marina' },
  { id: 'social', label: 'Social' },
  { id: 'racing', label: 'Racing' },
  { id: 'staff', label: 'Staff' },
  { id: 'closure', label: 'Closed' },
  { id: 'other', label: 'Other' },
];

export const REPEATS = [
  { id: 'none', label: 'Does not repeat' },
  { id: 'weekly', label: 'Every week' },
  { id: 'monthly', label: 'Every month (same date)' },
  { id: 'yearly', label: 'Every year' },
];

// The fields a person edits (the rest are stamps).
export const EVENT_FIELDS = ['title', 'date', 'endDate', 'allDay', 'start', 'end', 'repeat', 'repeatUntil', 'category', 'location', 'notes'];

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

// ---------- day arithmetic on 'YYYY-MM-DD' (UTC, so no DST surprises) ----------

function toUTC(day) {
  const [y, m, d] = day.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}
function fromUTC(ms) {
  return new Date(ms).toISOString().slice(0, 10);
}
export function addDays(day, n) {
  return fromUTC(toUTC(day) + n * 86400000);
}
export function daysBetween(a, b) {
  return Math.round((toUTC(b) - toUTC(a)) / 86400000);
}
function daysInMonth(y, m) { // m: 1-12
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

// ---------- the record ----------

/** A form's values → a clean record (only the fields that apply), or { error }. */
export function eventFromForm(v) {
  const title = String(v.title || '').trim();
  if (!title) return { error: 'Give the event a name.' };
  if (title.length > 120) return { error: 'Keep the name under 120 characters.' };
  const date = String(v.date || '');
  if (!DAY_RE.test(date)) return { error: 'Pick a date.' };
  const allDay = !!v.allDay;
  const out = {
    title, date, allDay,
    repeat: REPEATS.some(r => r.id === v.repeat) ? v.repeat : 'none',
    category: EVENT_CATEGORIES.some(c => c.id === v.category) ? v.category : 'marina',
    location: String(v.location || '').trim().slice(0, 200),
    notes: String(v.notes || '').trim().slice(0, 2000),
    endDate: '', start: '', end: '', repeatUntil: '',
  };
  if (allDay) {
    const endDate = String(v.endDate || '');
    if (endDate && endDate !== date) {
      if (!DAY_RE.test(endDate) || endDate < date) return { error: 'The last day can\'t be before the first.' };
      if (daysBetween(date, endDate) > 60) return { error: 'An event can run at most 60 days.' };
      out.endDate = endDate;
    }
  } else {
    const start = String(v.start || '');
    const end = String(v.end || '');
    if (!TIME_RE.test(start)) return { error: 'Pick a start time, or tick All day.' };
    if (end && !TIME_RE.test(end)) return { error: 'The end time isn\'t a time.' };
    if (end && end < start) return { error: 'The end time is before the start time.' };
    out.start = start;
    out.end = end;
  }
  if (out.repeat !== 'none' && v.repeatUntil) {
    if (!DAY_RE.test(v.repeatUntil) || v.repeatUntil < date) return { error: 'Repeat until must be on or after the first date.' };
    out.repeatUntil = v.repeatUntil;
  }
  return out;
}

/** Only the fields that differ between the record as opened and the form (collaboration rule). */
export function changedEventFields(before, after) {
  const out = {};
  EVENT_FIELDS.forEach(f => {
    const a = before?.[f] ?? (f === 'allDay' ? false : f === 'repeat' ? 'none' : '');
    const b = after[f] ?? '';
    if (a !== b) out[f] = b;
  });
  return out;
}

// ---------- occurrences ----------

/** Start days of `ev` that fall in [from, to] (repeats expanded, skipped days left out). */
function startDays(ev, from, to) {
  const span = ev.endDate && ev.endDate > ev.date ? daysBetween(ev.date, ev.endDate) : 0;
  const first = from > ev.date ? addDays(from, -span) : ev.date; // a multi-day event that started earlier still shows
  const last = ev.repeatUntil && ev.repeatUntil < to ? ev.repeatUntil : to;
  const skip = ev.skip || {};
  const days = [];
  const take = d => { if (d >= first && d <= last && d >= ev.date && !skip[d]) days.push(d); };
  const repeat = ev.repeat || 'none';
  if (repeat === 'none') {
    take(ev.date);
  } else if (repeat === 'weekly') {
    const n = Math.max(0, Math.ceil(daysBetween(ev.date, first) / 7));
    for (let d = addDays(ev.date, n * 7); d <= last; d = addDays(d, 7)) take(d);
  } else if (repeat === 'monthly' || repeat === 'yearly') {
    const [y0, m0, d0] = ev.date.split('-').map(Number);
    let [y, m] = first.split('-').map(Number);
    if (repeat === 'yearly') m = m0;
    if (`${y}-${String(m).padStart(2, '0')}` < ev.date.slice(0, 7)) { y = y0; m = m0; }
    for (let guard = 0; guard < 2000; guard++) {
      // A date the month doesn't have (31st, Feb 29) is skipped that month, as other calendars do.
      if (d0 <= daysInMonth(y, m)) {
        const d = `${y}-${String(m).padStart(2, '0')}-${String(d0).padStart(2, '0')}`;
        if (d > last) break;
        take(d);
      } else if (`${y}-${String(m).padStart(2, '0')}-01` > last) break;
      if (repeat === 'yearly') y++;
      else if (++m > 12) { m = 1; y++; }
    }
  }
  return { days, span };
}

/**
 * Every occurrence of the given events that touches [from, to], sorted by day then time.
 * Each: { key, id, day, lastDay, allDay, start, end, title, category, location, notes, repeat, source }.
 * `source` is 'events' for hand-entered ones; other parts of the app can add their own later.
 */
export function occurrences(events, from, to) {
  const out = [];
  Object.entries(events || {}).forEach(([id, ev]) => {
    if (!ev || !DAY_RE.test(ev.date || '') || !ev.title) return;
    const { days, span } = startDays(ev, from, to);
    days.forEach(day => {
      const lastDay = span ? addDays(day, span) : day;
      if (lastDay < from || day > to) return;
      out.push({
        key: `${id}@${day}`, id, day, lastDay, source: 'events',
        allDay: !!ev.allDay, start: ev.allDay ? '' : ev.start || '', end: ev.allDay ? '' : ev.end || '',
        title: ev.title, category: ev.category || 'marina', location: ev.location || '', notes: ev.notes || '',
        repeat: ev.repeat || 'none',
      });
    });
  });
  return out.sort(compareOccurrences);
}

export function compareOccurrences(a, b) {
  if (a.day !== b.day) return a.day < b.day ? -1 : 1;
  if (a.allDay !== b.allDay) return a.allDay ? -1 : 1; // all-day first, like a day view
  if (a.start !== b.start) return a.start < b.start ? -1 : 1;
  return a.title.localeCompare(b.title);
}

/**
 * A list view: a repeating event shows only its dates in the next `days` days (and always its next
 * one), so a weekly meeting doesn't fill four months of the list. One-off events are all kept.
 */
export function trimRepeats(list, from, days = 14) {
  const until = addDays(from, days);
  const seen = new Set();
  return list.filter(o => {
    if (o.repeat === 'none') return true;
    const first = !seen.has(`${o.source}:${o.id}`);
    seen.add(`${o.source}:${o.id}`);
    return first || o.day <= until;
  });
}

/** How many different events a list holds (a weekly one counts once). */
export function countEvents(list) {
  return new Set(list.map(o => `${o.source}:${o.id}`)).size;
}

/** Group occurrences under the day they show on: their own day, or `from` for one already under way. */
export function groupByDay(list, from) {
  const groups = new Map();
  list.forEach(o => {
    const day = o.day < from ? from : o.day;
    if (!groups.has(day)) groups.set(day, []);
    groups.get(day).push(o);
  });
  return [...groups.entries()].sort(([a], [b]) => (a < b ? -1 : 1));
}

/** How many occurrences touch `day` (the rail's "N today"). */
export function countOn(list, day) {
  return list.filter(o => o.day <= day && o.lastDay >= day).length;
}

// ---------- words ----------

/** '14:30' → '2:30 PM'. */
export function timeLabel(t) {
  if (!TIME_RE.test(t || '')) return '';
  const [h, m] = t.split(':').map(Number);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

/** The chip text: ALL DAY, a time, or a range. */
export function whenLabel(o) {
  if (o.allDay) return 'ALL DAY';
  const s = timeLabel(o.start);
  const e = timeLabel(o.end);
  return e && e !== s ? `${s} – ${e}` : s;
}

export function repeatLabel(ev) {
  const r = REPEATS.find(x => x.id === (ev.repeat || 'none'));
  if (!r || r.id === 'none') return '';
  return ev.repeatUntil ? `${r.label}, until ${ev.repeatUntil}` : r.label;
}

export function categoryLabel(id) {
  return (EVENT_CATEGORIES.find(c => c.id === id) || EVENT_CATEGORIES[0]).label;
}
